import type { AxiosInstance } from 'axios';

import type { RfqStatus } from '@accio3d/shared-ui/lib/rfqStatus';
import type { FunctionalRequirements } from '@accio3d/shared-ui/printability/services/printability3dService';
import { fetchPaginatedList } from '@accio3d/shared-ui/services/pagination';
import type { PagePaginatedResponse, PaginationParams } from '@accio3d/shared-ui/types/pagination';
import type { PurchaserQuoteListItem } from './quotes';
import type {
  RfqInfoRequest,
  RfqMessage,
  RfqMessagesPage,
  RfqRequirement,
  RfqRequirementSummary,
} from './rfqs';

/** One part on a new RFQ. Mirrors `manufacturingRequirementSchema` on the API. */
export interface CreateRfqPartInput {
  partId: string;
  quantity?: number;
  material?: string;
  processType?: string;
  color?: string;
  infill?: number;
  postProcesses?: string[];
  tightestToleranceMm?: number;
  notes?: string;
  documentFileUrls?: string[];
  functionalRequirements?: FunctionalRequirements;
  currentPrice?: number;
  targetPrice?: number;
  priceSpreadsheet?: string;
  /**
   * Part geometry the purchaser confirmed, in millimetres and grams. Snapshotted
   * onto every RFQ in the group so suppliers can derive a parcel.
   */
  partLengthMm?: number;
  partWidthMm?: number;
  partHeightMm?: number;
  partVolumeMm3?: number;
  partMassG?: number;
}

export interface CreateRfqInput {
  name?: string;
  shippingAddress?: string;
  supplierLocation?: string;
  isReconstruction?: boolean;
  initialMessage?: string;
  /** Empty = queue for admin matching; non-empty fans out immediately. */
  supplierIds?: string[];
  parts: CreateRfqPartInput[];
}

/** The purchaser's resumable draft RFQ. `payload` mirrors the wizard's RfqDraft shape. */
export interface PurchaserRfqDraft {
  id: string;
  payload: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

/** One supplier's pairing on a purchaser RFQ — its own status, quotes, and thread. */
export interface PurchaserRfqSupplier {
  /** The `supplier_rfqs` row id — pass this to message/quote routes, not the RFQ id. */
  id: string;
  supplierName: string | null;
  status: RfqStatus;
  /** Quotes this supplier has SENT — drafts are not visible to the purchaser. */
  quoteCount: number;
  lastMessage: string | null;
  lastMessageAt: string | null;
  createdAt: string;
}

// Mirrors PurchasersService.toRfqDto: one RFQ send, its parts, and every
// matched supplier's pairing.
export interface PurchaserRfq {
  id: string;
  name: string | null;
  initialMessage: string | null;
  manufacturingRequirements: RfqRequirementSummary[];
  suppliers: PurchaserRfqSupplier[];
  supplierCount: number;
  /** Sum of every supplier's sent-quote count. */
  quoteCount: number;
  /** Rollup over every supplier pairing — `matching` until at least one is matched. */
  status: RfqStatus;
  rfqNumber: string;
  shippingAddress: string | null;
  supplierLocation: string | null;
  isReconstruction: boolean;
  purchaserId: string | null;
  createdAt: string;
  updatedAt: string;
}

export function createPurchaserRfqsService(client: AxiosInstance) {
  return {
    async createRfq(body: CreateRfqInput): Promise<PurchaserRfq> {
      const { data } = await client.post<PurchaserRfq>('/purchasers/rfqs', body);
      return data;
    },

    async list(
      pagination?: PaginationParams,
      filters?: { search?: string; status?: string },
    ): Promise<PagePaginatedResponse<PurchaserRfq>> {
      const params: Record<string, string> = {};
      if (filters?.search) params.search = filters.search;
      if (filters?.status) params.status = filters.status;
      return fetchPaginatedList<PurchaserRfq>(client, '/purchasers/rfqs', pagination, {
        params,
      });
    },

    /** RFQ counts per status, for the RFQ-list stat cards. */
    async stats(): Promise<Record<RfqStatus, number>> {
      const { data } = await client.get<Record<RfqStatus, number>>('/purchasers/rfqs/stats');
      return data;
    },

    async get(id: string): Promise<PurchaserRfq> {
      const { data } = await client.get<PurchaserRfq>(`/purchasers/rfqs/${encodeURIComponent(id)}`);
      return data;
    },

    async listRequirements(rfqId: string): Promise<RfqRequirement[]> {
      const { data } = await client.get<RfqRequirement[]>(
        `/purchasers/rfqs/${encodeURIComponent(rfqId)}/requirements`,
      );
      return data;
    },

    /** Questions suppliers have asked about this RFQ, oldest first. */
    async infoRequests(rfqId: string): Promise<RfqInfoRequest[]> {
      const { data } = await client.get<RfqInfoRequest[]>(
        `/purchasers/rfqs/${encodeURIComponent(rfqId)}/info-requests`,
      );
      return data;
    },

    async answerInfoRequest(requestId: string, answer: string): Promise<RfqInfoRequest> {
      const { data } = await client.post<RfqInfoRequest>(
        `/purchasers/rfqs/info-requests/${encodeURIComponent(requestId)}/answer`,
        { answer },
      );
      return data;
    },

    /** Messages on one supplier's thread — `supplierRfqId` is `PurchaserRfqSupplier.id`, not the RFQ id. */
    async listMessages(
      supplierRfqId: string,
      params?: { cursor?: string },
    ): Promise<RfqMessagesPage> {
      const { data } = await client.get<RfqMessagesPage>(
        `/purchasers/supplier-rfqs/${encodeURIComponent(supplierRfqId)}/messages`,
        { params },
      );
      return data;
    },

    async sendMessage(supplierRfqId: string, body: string): Promise<RfqMessage> {
      const { data } = await client.post<RfqMessage>(
        `/purchasers/supplier-rfqs/${encodeURIComponent(supplierRfqId)}/messages`,
        { body },
      );
      return data;
    },

    /** Quotes received against one RFQ. */
    async quotes(
      rfqId: string,
      pagination?: PaginationParams,
    ): Promise<PagePaginatedResponse<PurchaserQuoteListItem>> {
      return fetchPaginatedList<PurchaserQuoteListItem>(
        client,
        `/purchasers/rfqs/${encodeURIComponent(rfqId)}/quotes`,
        pagination,
      );
    },

    /** The purchaser's resumable draft RFQ, or null if none has been saved. */
    async getDraft(): Promise<PurchaserRfqDraft | null> {
      const { data } = await client.get<PurchaserRfqDraft | null>('/purchasers/rfq-draft');
      return data;
    },

    async saveDraft(payload: Record<string, unknown>): Promise<PurchaserRfqDraft> {
      const { data } = await client.put<PurchaserRfqDraft>('/purchasers/rfq-draft', { payload });
      return data;
    },

    async deleteDraft(): Promise<void> {
      await client.delete('/purchasers/rfq-draft');
    },
  };
}

export type PurchaserRfqsService = ReturnType<typeof createPurchaserRfqsService>;
